// The field views' bottom sheet (2026-10-08, phone feedback: "the layers
// button looks like it's covered by the nearby assets table - can that table
// be collapsible?"). On a phone the map fills the screen and the list sits
// in a sheet over its foot with a drag handle and three states:
//   collapsed - one peek row: how many assets, and the nearest;
//   half      - the list over the lower ~45 % of the map;
//   full      - everything, but never over the map controls (Layers, Area,
//               Follow, Aerial): the sheet stops below them.
// The state is remembered per view. Pure (no DOM): frontend/tests/sheet.test.ts.

export type SheetState = "collapsed" | "half" | "full";
export const SHEET_STATES: SheetState[] = ["collapsed", "half", "full"];

export interface SheetHeights {
  collapsed: number;
  half: number;
  full: number;
}

/** Peek row height (handle + one line), px. */
export const PEEK_PX = 64;

/** Heights for a body `bodyH` px tall whose top `reservedTop` px (the map
 * controls, plus a margin) must stay uncovered. */
export function sheetHeights(bodyH: number, reservedTop: number, peek = PEEK_PX): SheetHeights {
  const full = Math.max(peek, Math.round(bodyH - reservedTop));
  const half = Math.min(full, Math.max(peek, Math.round(bodyH * 0.45)));
  return { collapsed: Math.min(peek, full), half, full };
}

/** Where a drag ends: the nearest state, or the next one along a fling
 * (velocity in px/ms, positive = the sheet growing). */
export function snapState(height: number, hs: SheetHeights, velocity = 0): SheetState {
  const order = SHEET_STATES.map((s) => [s, hs[s]] as const);
  if (Math.abs(velocity) > 0.5) {
    if (velocity > 0) return (order.find(([, v]) => v > height + 1)?.[0] ?? "full");
    return ([...order].reverse().find(([, v]) => v < height - 1)?.[0] ?? "collapsed");
  }
  let best: SheetState = "half";
  let bd = Infinity;
  for (const [s, v] of order) {
    const d = Math.abs(v - height);
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

/** A tap on the handle: collapsed <-> half; full -> half. */
export function tapState(s: SheetState): SheetState {
  return s === "collapsed" ? "half" : s === "half" ? "collapsed" : "half";
}

/** Arrow keys on the handle: one state up or down. */
export function stepState(s: SheetState, dir: 1 | -1): SheetState {
  const i = SHEET_STATES.indexOf(s) + dir;
  return SHEET_STATES[Math.max(0, Math.min(SHEET_STATES.length - 1, i))];
}

export function clampHeight(h: number, hs: SheetHeights): number {
  return Math.max(hs.collapsed, Math.min(hs.full, h));
}

/** The state remembered for a view (default per view). */
export function storedState(raw: string | null, fallback: SheetState): SheetState {
  return raw === "collapsed" || raw === "half" || raw === "full" ? raw : fallback;
}
